import React, { useRef, useState } from "react";
import { Pencil } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useContentApi } from "../context/ContentContext";
import { toast } from "sonner";

/**
 * Editable text node.
 * - Renders a plain element (default `span`) for guests/non-admins.
 * - For admins, the element becomes contentEditable with hover ring + save-on-blur.
 *
 * Usage:
 *   <Editable contentKey="hero.title1" as="span">Wear the spirit.</Editable>
 */
export const Editable = ({
    contentKey,
    as: Tag = "span",
    children,
    className = "",
    multiline = false,
    ...rest
}) => {
    const { user } = useAuth();
    const { c, saveContent } = useContentApi();
    const ref = useRef(null);
    const [saving, setSaving] = useState(false);

    const fallback = typeof children === "string" ? children : "";
    const text = c(contentKey, fallback);

    const isAdmin = user?.role === "admin";

    if (!isAdmin) {
        return (
            <Tag className={className} {...rest}>
                {text}
            </Tag>
        );
    }

    const handleBlur = async () => {
        const newVal = (ref.current?.innerText || "").trim();
        if (newVal === text) return;
        setSaving(true);
        try {
            await saveContent(contentKey, newVal);
            toast.success("Saved");
        } catch {
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (e) => {
        if (!multiline && e.key === "Enter") {
            e.preventDefault();
            ref.current?.blur();
        }
        if (e.key === "Escape") {
            if (ref.current) ref.current.innerText = text;
            ref.current?.blur();
        }
    };

    return (
        <Tag
            ref={ref}
            contentEditable
            suppressContentEditableWarning
            data-testid={`editable-${contentKey}`}
            data-editable-key={contentKey}
            spellCheck={true}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`${className} editable-text ${saving ? "editable-saving" : ""}`}
            {...rest}
        >
            {text}
        </Tag>
    );
};

/**
 * Editable image — admin clicks → file picker → uploads via /api/upload → swaps URL.
 */
export const EditableImage = ({ contentKey, alt = "", className = "", fallbackSrc = "" }) => {
    const { user } = useAuth();
    const { c, saveContent } = useContentApi();
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const src = c(contentKey, fallbackSrc);
    const isAdmin = user?.role === "admin";

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await (await import("../lib/api")).default.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await saveContent(contentKey, data.url);
            toast.success("Image updated");
        } catch {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    if (!isAdmin) {
        return <img src={src} alt={alt} className={className} loading="lazy" />;
    }

    return (
        <div className="relative group">
            <img
                src={src}
                alt={alt}
                className={className}
                loading="lazy"
                data-testid={`editable-image-${contentKey}`}
            />
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                data-testid={`editable-image-btn-${contentKey}`}
                aria-label={`Replace image (${contentKey})`}
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-[#1C252E] hover:bg-[#E53935] hover:text-white rounded-full px-3 py-1.5 text-xs font-medium shadow-md opacity-0 group-hover:opacity-100 transition-all disabled:opacity-100"
            >
                <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
                {uploading ? "Uploading…" : "Replace"}
            </button>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
            />
        </div>
    );
};

export default Editable;
